import {
    deleteVacancyWithReason,
    getAvailableEmployeesQuiet,
    getExchangeList,
    getOutsourceReportQuiet,
    getVacancyHistoryQuiet,
    getVacancySummary,
    patchRejectRequest,
    postConfirmAssignation,
    postRejectResponse,
    postVacancy,
    postVacancyBatch,
    putVacancy,
} from '@/api/exchange'
import { exchangeTypeOf } from './exchangeFormat'
import type {
    AvailableEmployee,
    BatchResponse,
    ExchangeError,
    ExchangeSummary,
    ExchangeView,
    HistoryRecord,
    ListQuery,
    NamedRef,
    Paged,
    RawVacancy,
    UserVacancy,
    Vacancy,
} from './exchangeTypes'

// Режим данных биржи (RD-164, integration-requirements: подключение RD-165).
// real — по умолчанию и единственный штатный: все операции идут в развёрнутый main-backend RD-165.
// scenario — демонстрационные fixtures для явного dev-сценария: только dev-сборка и
// VITE_EXCHANGE_MOCK=scenario; синтетические ID, ни одного сетевого запроса биржи. Ошибки real-режима
// fixtures не включают.
export const EXCHANGE_MOCK_ENABLED = import.meta.env.DEV && import.meta.env.VITE_EXCHANGE_MOCK === 'scenario'

export interface ExchangeBackend {
    list(view: ExchangeView, query: ListQuery): Promise<Paged<RawVacancy>>
    summary(view: ExchangeView, query: ListQuery): Promise<ExchangeSummary>
    createOne(payload: Record<string, unknown>): Promise<RawVacancy | null>
    createBatch(payload: Record<string, unknown>, idempotencyKey: string): Promise<BatchResponse>
    confirm(id: string, userId: string): Promise<string | null>
    rejectResponse(id: string, userId: string): Promise<string | null>
    rejectRequest(id: string): Promise<void>
    update(id: string, payload: Record<string, unknown>): Promise<void>
    remove(id: string, reason: string): Promise<void>
    history(id: string): Promise<HistoryRecord[]>
    availableEmployees(departmentId: string, start: string, end: string): Promise<AvailableEmployee[]>
    report(data: Record<string, unknown>): Promise<Blob>
}

const LIST_PATHS: Record<ExchangeView, 'created-by-me' | 'requests' | 'archive'> = {
    vacancies: 'created-by-me',
    requests: 'requests',
    archive: 'archive',
}

type Params = Record<string, string | number | boolean>

// Параметры фильтров (target-contract §4). ordering не передаётся: он заменяет серверный порядок.
function filterParams(query: ListQuery): Params {
    const params: Params = {
        dt_from: query.period[0],
        dt_to: query.period[1],
    }
    const search = query.search.trim()
    if (search) params.search = search
    if (query.positionIds.length) params.position_ids = query.positionIds.join(',')
    if (query.isUrgent !== null) params.is_urgent = query.isUrgent
    if (query.exchangeType) params.exchange_type = query.exchangeType
    if (query.recordType) params.record_type = query.recordType
    if (query.result?.length) params.result = query.result.join(',')
    if (query.lifecycle?.length) params.lifecycle = query.lifecycle.join(',')
    return params
}

export function listParams(view: ExchangeView, query: ListQuery): Params {
    const params: Params = { page: query.page, page_size: query.pageSize, ...filterParams(query) }
    if (view === 'vacancies' && query.segment) params.segment = query.segment
    if (view === 'requests' && query.direction) params.direction = query.direction
    return params
}

export function summaryParams(view: ExchangeView, query: ListQuery): Params {
    return { view, ...filterParams(query) }
}

function toError(error: unknown): ExchangeError {
    if (isExchangeError(error)) return error
    const anyError = error as { response?: { status?: number, data?: any } }
    const response = anyError?.response
    if (!response) {
        return { kind: 'network', status: null, code: null, message: 'Нет связи с сервером', body: null }
    }
    const status = response.status ?? null
    const data = response.data && typeof response.data === 'object' && !(response.data instanceof Blob) ? response.data : {}
    const message = typeof data.msg === 'string' ? data.msg : typeof data.detail === 'string' ? data.detail : ''
    if (status !== null && status >= 500) {
        return { kind: 'network', status, code: data.code ?? null, message: message || 'Ошибка сервера', body: data.body ?? null }
    }
    // 404/405 — обычная ошибка: несовместимый backend не эмулируется локально (integration-requirements).
    return { kind: 'http', status, code: data.code ?? null, message, body: data.body ?? null }
}

export function isExchangeError(value: unknown): value is ExchangeError {
    return Boolean(value && typeof value === 'object' && 'kind' in value && 'message' in value && 'code' in value)
}

// Рабочий envelope: status=success (summary отвечает status=ok) → body; status=error при HTTP 200 — тоже ошибка.
async function envelope<T>(request: Promise<{ status: number, data: any }>): Promise<{ body: T, msg: string | null, status: number }> {
    let res
    try {
        res = await request
    } catch (error) {
        throw toError(error)
    }
    const data = res.data ?? {}
    if (data.status === 'error') {
        throw { kind: 'http', status: res.status, code: data.code ?? null, message: data.msg ?? '', body: data.body ?? null } satisfies ExchangeError
    }
    return { body: data.body as T, msg: typeof data.msg === 'string' ? data.msg : null, status: res.status }
}

const realBackend: ExchangeBackend = {
    async list(view, query) {
        const { body } = await envelope<Paged<RawVacancy>>(getExchangeList(LIST_PATHS[view], listParams(view, query)))
        return { count: body?.count ?? 0, next: body?.next ?? null, previous: body?.previous ?? null, results: body?.results ?? [] }
    },
    async summary(view, query) {
        const { body } = await envelope<ExchangeSummary>(getVacancySummary(summaryParams(view, query)))
        return body
    },
    async createOne(payload) {
        const { body } = await envelope<RawVacancy | null>(postVacancy(payload))
        return body ?? null
    },
    async createBatch(payload, key) {
        const { body } = await envelope<BatchResponse>(postVacancyBatch(payload, key))
        return body
    },
    async confirm(id, userId) {
        return (await envelope<unknown>(postConfirmAssignation(id, userId))).msg
    },
    async rejectResponse(id, userId) {
        return (await envelope<unknown>(postRejectResponse(id, userId))).msg
    },
    async rejectRequest(id) {
        await envelope<unknown>(patchRejectRequest(id))
    },
    async update(id, payload) {
        await envelope<unknown>(putVacancy(id, payload))
    },
    async remove(id, reason) {
        // DELETE отвечает 204 без тела.
        await envelope<unknown>(deleteVacancyWithReason(id, reason))
    },
    async history(id) {
        const { body } = await envelope<HistoryRecord[] | Paged<HistoryRecord>>(getVacancyHistoryQuiet(id))
        // Рабочий клиентский envelope — body[]; OpenAPI описывает пагинацию. Иное не подменяется молча.
        if (Array.isArray(body)) return body
        if (body && Array.isArray((body as Paged<HistoryRecord>).results)) return (body as Paged<HistoryRecord>).results
        return []
    },
    async availableEmployees(departmentId, start, end) {
        const { body } = await envelope<AvailableEmployee[]>(getAvailableEmployeesQuiet(departmentId, start, end))
        return Array.isArray(body) ? body : []
    },
    async report(data) {
        try {
            const res = await getOutsourceReportQuiet(data)
            return res.data as Blob
        } catch (error) {
            throw toError(error)
        }
    },
}

let mockBackend: Promise<ExchangeBackend> | null = null

async function backend(): Promise<ExchangeBackend> {
    if (!EXCHANGE_MOCK_ENABLED) return realBackend
    // Модуль сценария попадает только в dev-сборку с явным флагом.
    mockBackend ??= import('./mock/exchangeMockScenario').then(module => module.createMockBackend())
    return mockBackend
}

export const exchangeApi: ExchangeBackend = {
    list: async (...args) => (await backend()).list(...args),
    summary: async (...args) => (await backend()).summary(...args),
    createOne: async (...args) => (await backend()).createOne(...args),
    createBatch: async (...args) => (await backend()).createBatch(...args),
    confirm: async (...args) => (await backend()).confirm(...args),
    rejectResponse: async (...args) => (await backend()).rejectResponse(...args),
    rejectRequest: async (...args) => (await backend()).rejectRequest(...args),
    update: async (...args) => (await backend()).update(...args),
    remove: async (...args) => (await backend()).remove(...args),
    history: async (...args) => (await backend()).history(...args),
    availableEmployees: async (...args) => (await backend()).availableEmployees(...args),
    report: async (...args) => (await backend()).report(...args),
}

function refOf(value: string | NamedRef | null | undefined): { id: string | null, name: string | null } {
    if (!value) return { id: null, name: null }
    if (typeof value === 'string') return { id: value, name: null }
    return { id: value.id ?? null, name: value.name ?? null }
}

function userOf(value: string | UserVacancy | null | undefined): { id: string | null, user: UserVacancy | null } {
    if (!value) return { id: null, user: null }
    if (typeof value === 'string') return { id: value, user: null }
    return { id: value.id ?? null, user: value }
}

/** DTO → модель строки. Новые факты — только из полей RD-165, без восстановления по старым полям. */
export function normalizeVacancy(raw: RawVacancy): Vacancy {
    const inner = raw.inner_exchange ?? true
    const outer = raw.outer_exchange ?? false
    const requestedDepartment = refOf(raw.requested_department)
    const requestedUser = userOf(raw.requested_user)
    const requestedDepartmentId = raw.requested_department_id ?? requestedDepartment.id
    const requestedUserId = raw.requested_user_id ?? requestedUser.id
    // responses=[] без права can_confirm_user_for_vacancy не доказывает отсутствия откликов:
    // кандидаты не восстанавливаются из responded_users, has_ever_received_response не сбрасывается.
    const responses = (raw.responses ?? []).map(item => ({ id: item.id, user: item.user, createdAt: item.created_at, source: item.source }))
    return {
        id: raw.id,
        raw,
        position: raw.position ?? { id: '', name: '' },
        department: raw.department ?? { id: '', name: '' },
        date: raw.date,
        start: raw.dttm_work_start_at,
        end: raw.dttm_work_end_at,
        comment: raw.comment ?? '',
        createdAt: raw.created_at ?? null,
        createdBy: raw.created_by ?? null,
        status: raw.status ?? null,
        isRequestRejected: Boolean(raw.is_request_rejected),
        inner,
        outer,
        exchangeType: exchangeTypeOf(inner, outer),
        responses,
        assigned: raw.assigned_user ?? null,
        hasEverReceivedResponse: raw.has_ever_received_response,
        // closed + assigned_shift_id=null — допустимая историческая запись (RD-165 1.2), lifecycle не пересчитывается.
        assignedShiftId: raw.assigned_shift_id,
        canEdit: raw.can_edit === true,
        canDelete: raw.can_delete === true,
        lifecycle: raw.lifecycle ?? null,
        archiveResult: raw.archive_result ?? null,
        deletionReason: raw.deletion_reason ?? null,
        isRequest: Boolean(requestedDepartmentId || requestedUserId),
        requestedDepartmentId,
        requestedDepartmentName: requestedDepartment.name,
        requestedUserId,
        requestedUser: requestedUser.user,
    }
}
